import { ColumnTable, Icon } from '@octano/global-ui';
import DocumentBadgeState from '../parts/DocumentBadgeState';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { TranslationsKeys } from '../../../locales/translations';
import { DegreeProcessDocument } from '../types';
import AzureFilesUploader from '../../../components/AzureFilesUploader/AzureFilesUploader';

type UseDegreeDocumentsColumnsProps = {
  uploadingId?: number | null;
  deletingId?: number | null;
  onUpload?: (document: DegreeProcessDocument, fileId: number) => void;
  onDelete?: (document: DegreeProcessDocument) => void;
};

export const useDegreeDocumentsColumns = ({
  uploadingId = null,
  deletingId = null,
  onUpload,
  onDelete,
}: UseDegreeDocumentsColumnsProps): Array<
  ColumnTable<DegreeProcessDocument>
> => {
  const { t } = useTranslation(TranslationsKeys.DEGREE_POOCESSES_DOCUMENTS);

  const isDisabled = useCallback(
    (row: DegreeProcessDocument) =>
      !row?.updateAllowed ||
      !!deletingId ||
      (uploadingId !== row?.id && !!uploadingId),
    [uploadingId, deletingId],
  );

  const isUploading = useCallback(
    (row: DegreeProcessDocument) => !!uploadingId && uploadingId === row?.id,
    [uploadingId],
  );

  const isDeleting = useCallback(
    (row: DegreeProcessDocument) => !!deletingId && deletingId === row?.id,
    [deletingId],
  );

  return [
    {
      columnName: 'name',
      thClassName: 'text-center',
      tdClassName: 'text-center',
      headerText: t('headers.type'),
      width: 250,
    },
    {
      columnName: 'file',
      thClassName: 'text-center',
      tdClassName: 'text-center',
      headerText: t('headers.name'),
      cellFormat: ({ row }) =>
        isDeleting(row) ? (
          <Icon className="spin" name="circle" color="secondary" size="20px" />
        ) : row?.file?.url ? (
          <div>
            {`${row?.file?.name}.${row?.file?.extension}`}
            <a
              className="btn btn-sm ml-2"
              href={row?.file?.url}
              target="_blank"
              rel="noreferrer"
            >
              <Icon name="download" />
            </a>
            <button
              className="btn btn-sm ml-2"
              type="button"
              disabled={isDisabled(row)}
              onClick={() => !!onDelete && onDelete(row)}
            >
              <Icon
                name="trash"
                color={isDisabled(row) ? 'disabled' : 'primary'}
              />
            </button>
          </div>
        ) : (
          <></>
        ),
    },
    {
      columnName: 'action',
      thClassName: 'text-center',
      tdClassName: 'text-center',
      headerText: t('headers.upload'),
      width: 170,
      cellFormat: ({ row }) => (
        <div style={{ width: 170 }}>
          <AzureFilesUploader
            hideLabel
            disabled={isDisabled(row)}
            loading={isUploading(row)}
            onUploaded={(e) => {
              !!onUpload && onUpload(row, e.id);
            }}
          />
        </div>
      ),
    },
    {
      columnName: 'status',
      thClassName: 'text-center',
      tdClassName: 'text-center',
      headerText: t('headers.status'),
      width: 170,
      cellFormat: ({ value }) => {
        return (
          <DocumentBadgeState status={value} text={t(`statuses.${value}`)} />
        );
      },
    },
  ];
};
